import React, { useState, useEffect } from 'react';
// Assuming a similar CSS file structure:
import './usertools-css/VitaminACarotenoidCalculator.css';


// --- RAE Conversion Factors (NIH/ODS standard) ---
const raeConversionFactors = {
  retinol_food_supp: 1,       // 1 µg retinol -> 1 µg RAE
  beta_carotene_supp: 1 / 2,  // 2 µg supplemental βC -> 1 µg RAE
  beta_carotene_food: 1 / 12, // 12 µg dietary βC -> 1 µg RAE
  alpha_carotene_food: 1 / 24,// 24 µg dietary αC -> 1 µg RAE
  beta_cryptoxanthin_food: 1 / 24, // 24 µg dietary β-cryptoxanthin -> 1 µg RAE
};

// --- Reference Intakes (IOM DRIs, µg RAE/day) ---
const vitARdaTable = {
  '0-6m': { target: 400, isAI: true },
  '7-12m': { target: 500, isAI: true },
  '1-3y': { target: 300 },
  '4-8y': { target: 400 },
  '9-13y': { target: 600 },
  '14-18y_female': { target: 700 },
  '14-18y_male': { target: 900 },
  '19+y_female': { target: 700 },
  '19+y_male': { target: 900 },
  'pregnant_14-18': { target: 750 },
  'pregnant_19+': { target: 770 },
  'lactating_14-18': { target: 1200 },
  'lactating_19+': { target: 1300 },
};

// --- Age-specific ULs for PRE-FORMED retinol (µg/day) ---
const retinolULByAge = (age) => {
  if (age < 1) return 600;
  if (age <= 3) return 600;
  if (age <= 8) return 900;
  if (age <= 13) return 1700;
  if (age <= 18) return 2800;
  return 3000; // adults
};

// --- Genetic Conversion Efficiency Factors (heuristic) ---
const bco1ConversionFactors = {
  unknown: 1.0,
  normal: 1.0,
  hetero: 0.8, // ~20% lower conversion
  homo: 0.6,   // ~40% lower conversion
};

// --- Absorption / Malabsorption Factors (heuristic knobs) ---
const absorptionFactors = {
  added_fat_boost: 0.20,      // +20% carotenoid absorption if eaten with fat
  malabsorption_penalty: 0.20 // -20% if malabsorption/bariatricHx
};

// Smoker warning threshold for β-carotene supplements
const smokerBetaCaroteneSuppWarnMg = 20; // mg/day

// --- Food Composition (raw µg per serving) ---
const foodVitAValues = {
  // Retinol sources
  beef_liver_3oz: { retinol: 6582, beta_carotene: 0, alpha_carotene: 0, beta_cryptoxanthin: 0 },
  eggs_1_large: { retinol: 75, beta_carotene: 0, alpha_carotene: 0, beta_cryptoxanthin: 0 }, // per egg
  milk_whole_cup: { retinol: 112, beta_carotene: 0, alpha_carotene: 0, beta_cryptoxanthin: 0 }, // varies w/ fortification
  cheese_cheddar_1oz: { retinol: 75, beta_carotene: 0, alpha_carotene: 0, beta_cryptoxanthin: 0 },
  salmon_3oz: { retinol: 50, beta_carotene: 0, alpha_carotene: 0, beta_cryptoxanthin: 0 }, // approx

  // Carotenoid sources (typical; food matrix matters)
  carrot_cooked_half_cup: { retinol: 0, beta_carotene: 6600, alpha_carotene: 3200, beta_cryptoxanthin: 0 },
  sweet_potato_cooked_half_cup: { retinol: 0, beta_carotene: 9400, alpha_carotene: 0, beta_cryptoxanthin: 0 },
  spinach_cooked_half_cup: { retinol: 0, beta_carotene: 5600, alpha_carotene: 0, beta_cryptoxanthin: 0 },
  kale_cooked_half_cup: { retinol: 0, beta_carotene: 4700, alpha_carotene: 0, beta_cryptoxanthin: 0 },
  mango_1_cup: { retinol: 0, beta_carotene: 1200, alpha_carotene: 20, beta_cryptoxanthin: 200 },
  pumpkin_puree_half_cup: { retinol: 0, beta_carotene: 6400, alpha_carotene: 4800, beta_cryptoxanthin: 0 },

  // Fortified placeholder
  fortified_margarine_tbsp: { retinol: 100, beta_carotene: 0, alpha_carotene: 0, beta_cryptoxanthin: 0 },
};

// --- Helper: map life-stage key ---
const getVitARdaKey = (ageNum, sex, isPregnant, isLactating) => {
  if (ageNum < 0.5) return '0-6m';
  if (ageNum < 1) return '7-12m';
  if (ageNum <= 3) return '1-3y';
  if (ageNum <= 8) return '4-8y';
  if (ageNum <= 13) return '9-13y';
  if (ageNum <= 18) {
    if (isPregnant) return 'pregnant_14-18';
    if (isLactating) return 'lactating_14-18';
    return sex === 'male' ? '14-18y_male' : '14-18y_female';
  }
  if (ageNum >= 19) {
    if (isPregnant) return 'pregnant_19+';
    if (isLactating) return 'lactating_19+';
    return sex === 'male' ? '19+y_male' : '19+y_female';
  }
  return '19+y_female';
};

// --- Component ---
function VitaminACarotenoidCalculator() {
  // Inputs
  const [age, setAge] = useState('');
  const [sex, setSex] = useState('');
  const [isPregnant, setIsPregnant] = useState(false);
  const [isLactating, setIsLactating] = useState(false);
  const [tryingToConceive, setTryingToConceive] = useState(false);
  const [isVegan, setIsVegan] = useState(false);

  // Diet diary (servings/day)
  const [dietServings, setDietServings] = useState({
    servings_liver: '',
    servings_eggs: '',            // counts INDIVIDUAL eggs
    servings_dairy: '',
    servings_fatty_fish: '',
    servings_carrots: '',
    servings_sweet_potato: '',
    servings_spinach: '',
    servings_kale: '',
    servings_mango: '',
    servings_pumpkin: '',
    servings_fortified_foods: '',
  });

  // Cooking & fat
  const [addsFatToVeg, setAddsFatToVeg] = useState(false);

  // Supplements
  const [takesSupplement, setTakesSupplement] = useState(false);
  const [supplementRetinolMcg, setSupplementRetinolMcg] = useState('');     // µg/day
  const [supplementBetaCaroteneMg, setSupplementBetaCaroteneMg] = useState(''); // mg/day

  // Genetics (simplified)
  const [bco1Genotype, setBco1Genotype] = useState('unknown'); // 'unknown' | 'normal' | 'hetero' | 'homo'

  // Smoking
  const [isSmoker, setIsSmoker] = useState(false);

  // Health flags
  const [healthFlags, setHealthFlags] = useState({
    liverDisease: false,
    highAlcoholIntake: false,
    fatMalabsorption: false,
    bariatricSurgery: false,
  });

  // Optional lab
  const [serumRetinol, setSerumRetinol] = useState(''); // nmol/L

  // Results
  const [calculationResult, setCalculationResult] = useState(null);
  const [showResults, setShowResults] = useState(false);

  // Derived
  const ageNum = parseInt(age, 10);
  const isFemale = sex === 'female';
  const userIsPregnantOrTrying = isPregnant || tryingToConceive;
  const hasMalabsorptionRisk = healthFlags.fatMalabsorption || healthFlags.bariatricSurgery;
  const hasLiverRisk = healthFlags.liverDisease || healthFlags.highAlcoholIntake;

  // Disable pregnancy/lactation toggles for inapplicable sex/age
  useEffect(() => {
    if (sex === 'male' || ageNum < 12 || ageNum > 55) {
      setIsPregnant(false);
      setIsLactating(false);
      setTryingToConceive(false);
    }
  }, [sex, ageNum]);

  // Handlers
  const handleDietChange = (event) => {
    const { name, value } = event.target;
    setDietServings(prev => ({ ...prev, [name]: value }));
  };
  const handleHealthFlagChange = (event) => {
    const { name, checked } = event.target;
    setHealthFlags(prev => ({ ...prev, [name]: checked }));
  };

  // --- Calculation ---
  const calculateVitaminAStatus = (e) => {
    e.preventDefault();
    setShowResults(false);
    setCalculationResult(null);

    let explanation = [];
    let safetyFlags = [];
    let suggestions = { food: [], supplement: [], lifestyle: [], genetic: [], general: [] };
    let targetRAE = 0;

    if (isNaN(ageNum) || !sex) {
      alert("Please fill in required fields: Age and Sex.");
      return;
    }

    // 1) Target (RDA/AI)
    const rdaKey = getVitARdaKey(ageNum, sex, isPregnant, isLactating);
    targetRAE = vitARdaTable[rdaKey]?.target || (sex === 'male' ? 900 : 700);
    const targetIsAI = vitARdaTable[rdaKey]?.isAI || false;
    explanation.push(`Base Vitamin A ${targetIsAI ? 'AI' : 'RDA'} Target (${rdaKey.replace('_', ' ')}): ${targetRAE} µg RAE/day.`);

    // Vegans: do NOT alter RDA; give practical tips
    if (isVegan) {
      suggestions.lifestyle.push(
        "Plant-only? Pair carotenoid-rich foods with some fat (oil, nuts, seeds, avocado) and cook/chop veg to improve absorption."
      );
    }

    // 2) Intake tally (raw µg/day)
    let retinol_food_mcg = 0;
    let beta_carotene_food_mcg = 0;
    let alpha_crypto_food_mcg = 0; // α-carotene + β-cryptoxanthin

    const getDietVal = (key) => parseFloat(dietServings[key]) || 0;

    const addFood = (servingKey, foodKey) => {
      const servings = getDietVal(servingKey);
      if (servings > 0 && foodVitAValues[foodKey]) {
        retinol_food_mcg += (foodVitAValues[foodKey].retinol || 0) * servings;
        beta_carotene_food_mcg += (foodVitAValues[foodKey].beta_carotene || 0) * servings;
        alpha_crypto_food_mcg += (foodVitAValues[foodKey].alpha_carotene || 0) * servings;
        alpha_crypto_food_mcg += (foodVitAValues[foodKey].beta_cryptoxanthin || 0) * servings;
      }
    };

    // Retinol foods
    addFood('servings_liver', 'beef_liver_3oz');
    addFood('servings_eggs', 'eggs_1_large'); // per-egg math
    addFood('servings_dairy', 'milk_whole_cup');
    addFood('servings_fatty_fish', 'salmon_3oz');
    // Carotenoid foods
    addFood('servings_carrots', 'carrot_cooked_half_cup');
    addFood('servings_sweet_potato', 'sweet_potato_cooked_half_cup');
    addFood('servings_spinach', 'spinach_cooked_half_cup');
    addFood('servings_kale', 'kale_cooked_half_cup');
    addFood('servings_mango', 'mango_1_cup');
    addFood('servings_pumpkin', 'pumpkin_puree_half_cup');

    // Fortified (proxy)
    const fortServings = getDietVal('servings_fortified_foods');
    retinol_food_mcg += fortServings * (foodVitAValues.fortified_margarine_tbsp.retinol || 0);

    explanation.push(
      `Estimated daily intake (raw µg): Retinol=${retinol_food_mcg.toFixed(0)}, β-carotene=${beta_carotene_food_mcg.toFixed(0)}, α-carotene/β-cryptoxanthin=${alpha_crypto_food_mcg.toFixed(0)}.`
    );

    // Supplements
    const retinol_supp_mcg = parseFloat(supplementRetinolMcg) || 0;
    const beta_carotene_supp_mg = parseFloat(supplementBetaCaroteneMg) || 0;
    const beta_carotene_supp_mcg = beta_carotene_supp_mg * 1000;
    if (takesSupplement) {
      explanation.push(
        `Supplement intake: Retinol=${retinol_supp_mcg.toFixed(0)} µg/d, β-carotene=${beta_carotene_supp_mg.toFixed(1)} mg/d (${beta_carotene_supp_mcg.toFixed(0)} µg).`
      );
    }

    // 3) Convert to RAE + adjustments
    const retinol_total_mcg = retinol_food_mcg + retinol_supp_mcg;
    const retinol_rae = retinol_total_mcg * raeConversionFactors.retinol_food_supp;
    explanation.push(`RAE from pre-formed retinol: ${retinol_rae.toFixed(0)} µg RAE.`);

    const food_beta_carotene_rae_unadjusted = beta_carotene_food_mcg * raeConversionFactors.beta_carotene_food;
    const food_alpha_crypto_rae_unadjusted = alpha_crypto_food_mcg * raeConversionFactors.alpha_carotene_food;
    const food_carot_rae_base = food_beta_carotene_rae_unadjusted + food_alpha_crypto_rae_unadjusted;

    const supp_beta_carotene_rae_unadjusted = beta_carotene_supp_mcg * raeConversionFactors.beta_carotene_supp;

    explanation.push(`Base RAE from food carotenoids: ${food_carot_rae_base.toFixed(0)} µg RAE.`);
    if (takesSupplement && beta_carotene_supp_mcg > 0) {
      explanation.push(`Base RAE from supplemental β-carotene: ${supp_beta_carotene_rae_unadjusted.toFixed(0)} µg RAE.`);
    }

    const fat_boost_factor = addsFatToVeg ? (1 + absorptionFactors.added_fat_boost) : 1.0;
    const food_carot_rae_fat_adjusted = food_carot_rae_base * fat_boost_factor;
    if (addsFatToVeg) {
      explanation.push(
        `Applied +${absorptionFactors.added_fat_boost * 100}% heuristic absorption boost for carotenoids eaten with fat: ${food_carot_rae_fat_adjusted.toFixed(0)} µg RAE.`
      );
    }

    const total_carot_rae_pre_genetic = food_carot_rae_fat_adjusted + supp_beta_carotene_rae_unadjusted;

    const genetic_conv_factor = bco1ConversionFactors[bco1Genotype] || 1.0;
    const effective_carot_rae = total_carot_rae_pre_genetic * genetic_conv_factor;
    if (genetic_conv_factor < 1.0) {
      explanation.push(
        `Carotenoid RAE adjusted for BCO1 (${bco1Genotype}) by ×${genetic_conv_factor.toFixed(1)}: ${effective_carot_rae.toFixed(0)} µg RAE.`
      );
      suggestions.genetic.push(
        `BCO1 (${bco1Genotype}) can reduce carotenoid→retinol conversion; emphasize carotenoid-rich foods and consider modest pre-formed sources if appropriate.`
      );
    } else {
      explanation.push(`Effective RAE from carotenoids (after fat/genetic): ${effective_carot_rae.toFixed(0)} µg RAE.`);
    }

    const total_rae_pre_malabsorption = retinol_rae + effective_carot_rae;

    const malabsorption_factor = hasMalabsorptionRisk ? (1.0 - absorptionFactors.malabsorption_penalty) : 1.0;
    const effective_total_RAE = total_rae_pre_malabsorption * malabsorption_factor;
    if (hasMalabsorptionRisk) {
      explanation.push(
        `Applied malabsorption penalty ×${malabsorption_factor.toFixed(1)}: ${effective_total_RAE.toFixed(0)} µg RAE.`
      );
      safetyFlags.push({
        type: 'info',
        text: 'Malabsorption/surgery noted. Vitamin A absorption may be reduced—monitor with your clinician.'
      });
    }

    explanation.push(`==> FINAL Estimated Effective Intake: ${effective_total_RAE.toFixed(0)} µg RAE/day.`);

    // 4) Gap & Safety
    const gap = targetRAE - effective_total_RAE;
    explanation.push(
      `Gap vs target: ${gap > 0 ? `${gap.toFixed(0)} µg RAE/day deficit` : `Adequate/Surplus (${Math.abs(gap).toFixed(0)} µg RAE).`}`
    );

    // UL (age-specific, pre-formed retinol only)
    const currentUL = retinolULByAge(ageNum);
    if (retinol_total_mcg > currentUL) {
      safetyFlags.push({
        type: 'severe',
        text: `RETINOL TOXICITY RISK: Pre-formed vitamin A ≈ ${retinol_total_mcg.toFixed(0)} µg > UL ${currentUL} µg/day.`
      });
    } else if (retinol_total_mcg > currentUL * 0.75) {
      safetyFlags.push({
        type: 'warning',
        text: `High pre-formed vitamin A: ≈ ${retinol_total_mcg.toFixed(0)} µg, approaching UL ${currentUL} µg/day.`
      });
    }

    // Pregnancy/trying: avoid supplements with pre-formed A; avoid liver
    if (userIsPregnantOrTrying) {
      if (retinol_supp_mcg > 0) {
        safetyFlags.push({
          type: 'warning',
          text: 'Pregnancy/Conception: Avoid supplements containing pre-formed vitamin A (retinol/retinyl esters). Discuss with your clinician.'
        });
      }
      const liverServ = getDietVal('servings_liver');
      if (liverServ > 0) {
        safetyFlags.push({
          type: 'warning',
          text: 'Pregnancy/Conception: Avoid liver/liver products due to very high retinol content.'
        });
      }
    }

    // Smoker + β-carotene supplement
    if (isSmoker && beta_carotene_supp_mg > smokerBetaCaroteneSuppWarnMg) {
      safetyFlags.push({
        type: 'warning',
        text: `Smoker & high β-carotene supplement (> ${smokerBetaCaroteneSuppWarnMg} mg/day): linked to higher lung cancer risk in trials.`
      });
    } else if (isSmoker && beta_carotene_supp_mg > 0) {
      safetyFlags.push({
        type: 'info',
        text: 'Smoker note: Prefer food sources of β-carotene over supplements.'
      });
    }

    // Liver risk
    if (hasLiverRisk) {
      safetyFlags.push({
        type: 'info',
        text: 'Liver disease/high alcohol intake can affect vitamin A storage and toxicity risk. Avoid high-dose supplements unless prescribed.'
      });
    }

    // Suggestions
    if (gap > 0) {
      suggestions.food.push(`Estimated shortfall: ${gap.toFixed(0)} µg RAE/day.`);
      suggestions.food.push(
        "Boost with: sweet potato (½ cup ≈ ~780 RAE), carrots (½ cup ≈ ~680 RAE), spinach (½ cup ≈ ~470 RAE), mango (1 cup ≈ ~110 RAE)."
      );
      if (!isVegan) {
        suggestions.food.push(
          "Consider modest retinol sources (e.g., eggs—~75 RAE each; fortified dairy). Use liver sparingly (3 oz beef liver ≈ ~6,600 RAE)."
        );
      }
    }

    // Serum retinol context (deficiency threshold only)
    const serumVal = parseFloat(serumRetinol);
    if (!isNaN(serumVal)) {
      explanation.push(`Reported serum retinol: ${serumVal.toFixed(2)} nmol/L.`);
      if (serumVal < 700) {
        safetyFlags.push({
          type: 'warning',
          text: `Low serum retinol (${serumVal.toFixed(2)} nmol/L; <700 suggests deficiency). Follow up with your clinician.`
        });
        suggestions.general.push('Low serum retinol reported—please follow up with your healthcare provider.');
      }
    }

    const percentRetinol = effective_total_RAE > 0 ? ((retinol_rae / (retinol_rae + effective_carot_rae)) * 100) : 0;
    const percentCarotenoid = effective_total_RAE > 0 ? ((effective_carot_rae / (retinol_rae + effective_carot_rae)) * 100) : 0;

    setCalculationResult({
      targetRAE: targetRAE.toFixed(0),
      effectiveTotalRAE: effective_total_RAE.toFixed(0),
      gap: gap.toFixed(0),
      totalRetinolIntakeMcg: retinol_total_mcg.toFixed(0),
      effectiveCarotenoidRAE: effective_carot_rae.toFixed(0),
      percentRetinol: percentRetinol.toFixed(0),
      percentCarotenoid: percentCarotenoid.toFixed(0),
      retinolUL: currentUL, // age-specific UL shown in UI
      explanation: explanation.join('\n'),
      suggestions,
      safetyFlags,
    });
    setShowResults(true);
  };

  // --- JSX ---
  return (
    <div className="vitaminACarotenoidCalculator">
      <h1>
        <span>
          <span role="img" aria-label="vitamin a calculator">
            🥕
          </span>{' '}
          Vitamin A Calculator
        </span>

        <span className="ai-promo-inline">
          <strong>Unlock Smarter Eating:</strong>{' '}
          Get a complete breakdown of vitamins, minerals, and fats plus a personalized
          weight-loss plan or nutrition tailored to your training with a single{' '}
          <a
            href="https://purehealthnexus.com/ai-nutrition-vitamins-minerals"
            className="ai-promo-link"
          >
            AI scan
          </a>.
        </span>
      </h1>

      <p className="important-note">
        <strong>Disclaimer:</strong> This calculator estimates Vitamin A intake in Retinol Activity Equivalents (RAE) from typical food values and standard conversion factors.
        Individual absorption/conversion vary with genetics (BCO1), diet, and health. Pre-formed vitamin A (retinol) has an age-specific UL (adults: 3,000 µg RAE/day);
        carotenoids from foods do not have a UL. This is educational, not medical advice—talk with your clinician, especially if pregnant/trying, vegan, have
        malabsorption or liver disease, or use supplements.
      </p>

      {/* --- Input Form --- */}
      <form onSubmit={calculateVitaminAStatus}>
        <h2>🧾 Your Information</h2>

        {/* Demographics */}
        <div className="form-section">
          <h3>👤 Demographics & Lifestyle</h3>
          <label>
            Age (Years):*
            <input type="number" value={age} onChange={(e) => setAge(e.target.value)} required min="0" max="120" placeholder="e.g., 30" />
          </label>
          <label>
            Sex Assigned at Birth:*
            <select value={sex} onChange={(e) => setSex(e.target.value)} required>
              <option value="">-- Select --</option>
              <option value="female">Female</option>
              <option value="male">Male</option>
            </select>
          </label>
          {isFemale && ageNum > 11 && ageNum < 55 && (
            <>
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={isPregnant}
                  onChange={(e) => setIsPregnant(e.target.checked)}
                  disabled={isLactating}
                />
                Currently Pregnant?
              </label>
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={isLactating}
                  onChange={(e) => setIsLactating(e.target.checked)}
                  disabled={isPregnant}
                />
                Currently Lactating?
              </label>
              <label className="checkbox-label alert-warning">
                <input
                  type="checkbox"
                  checked={tryingToConceive}
                  onChange={(e) => setTryingToConceive(e.target.checked)}
                  disabled={isPregnant || isLactating}
                />
                Planning / Trying to Conceive? (Important for retinol safety)
              </label>
            </>
          )}
          <label className="checkbox-label">
            <input type="checkbox" checked={isVegan} onChange={(e) => setIsVegan(e.target.checked)} />
            Follow a Strict Vegan Diet? (No animal products)
          </label>
          <label className="checkbox-label alert-warning">
            <input type="checkbox" checked={isSmoker} onChange={(e) => setIsSmoker(e.target.checked)} />
            Are you currently a smoker? (Important for supplement safety)
          </label>
        </div>

        {/* Diet Diary */}
        <div className="form-section">
          <h3>🍽️ Estimated Daily Food Intake (Servings)</h3>
          <p><small>Enter approximate number of servings PER DAY.</small></p>
          <div className="diet-grid vit-a-diet-grid">
            {/* Retinol Sources */}
            <div><h4>Retinol Sources (Animal/Fortified)</h4></div>
            <label>
              Liver (3oz servings):
              <input
                type="number"
                step="0.1"
                name="servings_liver"
                value={dietServings.servings_liver}
                onChange={handleDietChange}
                min="0"
                placeholder="e.g., 0.1 (if weekly)"
              />
            </label>
            <label>
              Eggs (count):
              <input
                type="number"
                name="servings_eggs"
                value={dietServings.servings_eggs}
                onChange={handleDietChange}
                min="0"
                placeholder="e.g., 2"
              />
            </label>
            <label>
              Dairy (cups/eq):
              <input
                type="number"
                name="servings_dairy"
                value={dietServings.servings_dairy}
                onChange={handleDietChange}
                min="0"
                placeholder="e.g., 1 (milk/yogurt)"
              />
            </label>
            <label>
              Fatty Fish (3oz):
              <input
                type="number"
                name="servings_fatty_fish"
                value={dietServings.servings_fatty_fish}
                onChange={handleDietChange}
                min="0"
                placeholder="e.g., 0.5"
              />
            </label>
            <label>
              Fortified Foods (servings):
              <input
                type="number"
                name="servings_fortified_foods"
                value={dietServings.servings_fortified_foods}
                onChange={handleDietChange}
                min="0"
                placeholder="e.g., 1 (margarine/cereal)"
              />
            </label>

            {/* Carotenoid Sources */}
            <div><h4>Carotenoid Sources (Plants)</h4></div>
            <label>
              Carrots (½ cup):
              <input
                type="number"
                step="0.5"
                name="servings_carrots"
                value={dietServings.servings_carrots}
                onChange={handleDietChange}
                min="0"
                placeholder="e.g., 1"
              />
            </label>
            <label>
              Sweet Potato (½ cup):
              <input
                type="number"
                step="0.5"
                name="servings_sweet_potato"
                value={dietServings.servings_sweet_potato}
                onChange={handleDietChange}
                min="0"
                placeholder="e.g., 1"
              />
            </label>
            <label>
              Spinach (cooked ½ cup):
              <input
                type="number"
                step="0.5"
                name="servings_spinach"
                value={dietServings.servings_spinach}
                onChange={handleDietChange}
                min="0"
                placeholder="e.g., 0.5"
              />
            </label>
            <label>
              Kale (cooked ½ cup):
              <input
                type="number"
                step="0.5"
                name="servings_kale"
                value={dietServings.servings_kale}
                onChange={handleDietChange}
                min="0"
                placeholder="e.g., 0.5"
              />
            </label>
            <label>
              Mango (cup):
              <input
                type="number"
                step="0.5"
                name="servings_mango"
                value={dietServings.servings_mango}
                onChange={handleDietChange}
                min="0"
                placeholder="e.g., 0"
              />
            </label>
            <label>
              Pumpkin (½ cup):
              <input
                type="number"
                step="0.5"
                name="servings_pumpkin"
                value={dietServings.servings_pumpkin}
                onChange={handleDietChange}
                min="0"
                placeholder="e.g., 0"
              />
            </label>
            <label className="checkbox-label full-width">
              <input type="checkbox" checked={addsFatToVeg} onChange={(e) => setAddsFatToVeg(e.target.checked)} />
              Do you typically eat vegetables/carotenoid sources with added fat/oil? (e.g., dressing, oil, nuts, avocado — boosts absorption)
            </label>
          </div>
        </div>

        {/* Supplements */}
        <div className="form-section">
          <h3>💊 Supplement Use (Average Daily Intake)</h3>
          <label className="checkbox-label">
            <input type="checkbox" checked={takesSupplement} onChange={(e) => setTakesSupplement(e.target.checked)} />
            Do you take any supplements containing Vitamin A or Beta-Carotene? (Check labels)
          </label>
          {takesSupplement && (
            <div className="supplement-details">
              <label>
                Pre-formed Vitamin A (Retinol, Palmitate, Acetate) per day (µg):
                <input
                  type="number"
                  value={supplementRetinolMcg}
                  onChange={(e) => setSupplementRetinolMcg(e.target.value)}
                  min="0"
                  placeholder="e.g., 750 (from label)"
                />
                <small>Enter total MICROGRAMS (µg) from all supplements.</small>
              </label>
              <label>
                Beta-Carotene per day (mg):
                <input
                  type="number"
                  step="any"
                  value={supplementBetaCaroteneMg}
                  onChange={(e) => setSupplementBetaCaroteneMg(e.target.value)}
                  min="0"
                  placeholder="e.g., 3 (from label)"
                />
                <small>Enter total MILLIGRAMS (mg) from all supplements.</small>
              </label>
            </div>
          )}
        </div>

        {/* Genetics */}
        <div className="form-section">
          <h3>🧬 Genetics (Optional)</h3>
          <label>
            Known BCO1 Genotype (Affects Carotenoid Conversion):
            <select value={bco1Genotype} onChange={(e) => setBco1Genotype(e.target.value)}>
              <option value="unknown">Unknown / Not Tested</option>
              <option value="normal">Normal Conversion (Typical)</option>
              <option value="hetero">Reduced Conversion (Heterozygous Variant)</option>
              <option value="homo">Lower Conversion (Homozygous Variant)</option>
            </select>
            <small>
              Select if you have genetic testing results for BCO1 variants (e.g., rs7501331, rs12934922). This affects how well carotenoids convert to retinol.
            </small>
          </label>
        </div>

        {/* Health Flags */}
        <div className="form-section">
          <h3>🩺 Health Factors</h3>
          <fieldset>
            <legend>Check any that apply:</legend>
            <label className="checkbox-label indented">
              <input
                type="checkbox"
                name="fatMalabsorption"
                checked={healthFlags.fatMalabsorption}
                onChange={handleHealthFlagChange}
              />
              Condition affecting fat absorption? (IBD, Celiac, Pancreatic issues)
            </label>
            <label className="checkbox-label indented">
              <input
                type="checkbox"
                name="bariatricSurgery"
                checked={healthFlags.bariatricSurgery}
                onChange={handleHealthFlagChange}
              />
              History of Bariatric Surgery?
            </label>
            <label className="checkbox-label indented alert-info">
              <input
                type="checkbox"
                name="liverDisease"
                checked={healthFlags.liverDisease}
                onChange={handleHealthFlagChange}
              />
              Diagnosed Liver Disease?
            </label>
            <label className="checkbox-label indented alert-info">
              <input
                type="checkbox"
                name="highAlcoholIntake"
                checked={healthFlags.highAlcoholIntake}
                onChange={handleHealthFlagChange}
              />
              Regular High Alcohol Intake?
            </label>
          </fieldset>
        </div>

        {/* Optional Lab */}
        <div className="form-section">
          <h3>🩸 Optional Lab Value (For Context)</h3>
          <label>
            Serum Retinol (if known, nmol/L):
            <input
              type="number"
              step="any"
              value={serumRetinol}
              onChange={(e) => setSerumRetinol(e.target.value)}
              placeholder="e.g., 1200"
            />
            <small>Deficiency often indicated by &lt; 700 nmol/L (0.70 µmol/L).</small>
          </label>
        </div>

        <button type="submit">Calculate Vitamin A (RAE) Status</button>
      </form>

      {/* --- Results Display --- */}
      {showResults && calculationResult && (
        <div className="results-section">
          <h2>📊 Estimated Vitamin A (RAE) Status</h2>

          {/* Safety Flags */}
          {calculationResult.safetyFlags.length > 0 && (
            <div className="safety-alerts">
              <h4>⚠️ Safety Alerts & Important Considerations</h4>
              <ul>
                {calculationResult.safetyFlags.map((flag, index) => (
                  <li key={index} className={`alert-${flag.type}`}>
                    <strong>{flag.type.toUpperCase()}:</strong> {flag.text}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Gauge & Pie (placeholder visuals driven by CSS) */}
          <div className="result-gauge-placeholder dual-gauge vit-a-gauge">
            <div className="gauge-pair">
              <p>Effective RAE vs Target</p>
              <div
                className="gauge-bar"
                style={{
                  '--effective-value': calculationResult.effectiveTotalRAE,
                  '--target-value': calculationResult.targetRAE,
                  '--max-value': Math.max(
                    Number(calculationResult.targetRAE) * 1.5,
                    Number(calculationResult.effectiveTotalRAE) * 1.1
                  )
                }}
              >
                <div className="gauge-fill"></div>
                <div className="gauge-target-line"></div>
              </div>
              <span className="gauge-label">
                Est. Effective: {calculationResult.effectiveTotalRAE} µg RAE / Target: {calculationResult.targetRAE} µg RAE
              </span>
              <span className={`gauge-status status-${calculationResult.gap > 0 ? 'low' : 'adequate'}`}>
                {calculationResult.gap > 0 ? `(~${calculationResult.gap} µg RAE deficit)` : 'Adequate'}
              </span>
            </div>

            <div className="pie-chart-placeholder">
              <p>RAE Source Breakdown</p>
              <div
                className="pie"
                style={{
                  '--p1': calculationResult.percentRetinol,
                  '--p2': calculationResult.percentCarotenoid
                }}
              >
                <span>
                  {calculationResult.percentRetinol}% Retinol
                  <br />
                  {calculationResult.percentCarotenoid}% Carotenoid
                </span>
              </div>
            </div>
          </div>

          <div className="results-grid">
            <div className="result-item">
              <span className="result-label">Target (RDA/AI):</span>
              <span className="result-value">{calculationResult.targetRAE} µg RAE/day</span>
            </div>
            <div className="result-item">
              <span className="result-label">Effective Intake:</span>
              <span className="result-value significant">{calculationResult.effectiveTotalRAE} µg RAE/day</span>
            </div>
            <div className="result-item">
              <span className="result-label">Intake vs Target:</span>
              <span className={`result-value status-${calculationResult.gap > 0 ? 'low' : 'adequate'}`}>
                {calculationResult.gap > 0 ? `Deficit (~${calculationResult.gap} µg RAE)` : `Adequate / Surplus`}
              </span>
            </div>
            <div className="result-item">
              <span className="result-label">Total Retinol Intake:</span>
              <span className="result-value">{calculationResult.totalRetinolIntakeMcg} µg/day</span>
              <small className="result-note">(UL = {calculationResult.retinolUL} µg)</small>
            </div>
            <div className="result-item">
              <span className="result-label">Carotenoid RAE:</span>
              <span className="result-value">{calculationResult.effectiveCarotenoidRAE} µg RAE/day</span>
              <small className="result-note">(After fat/genetic factors)</small>
            </div>
            <div className="result-item">
              <span className="result-label">RAE Source:</span>
              <span className="result-value">
                {calculationResult.percentRetinol}% Retinol / {calculationResult.percentCarotenoid}% Carotenoid
              </span>
            </div>
          </div>

          <h3>💡 Guidance & Suggestions</h3>
          <p className="gap-statement">
            {calculationResult.gap > 0
              ? `You appear to be about ${calculationResult.gap} µg RAE short of your personalized daily target.`
              : `Your estimated intake meets or exceeds your personalized daily target.`}
            {(calculationResult.totalRetinolIntakeMcg > calculationResult.retinolUL * 0.75) && (
              <span className="alert-warning"> Note: Your pre-formed retinol intake is high, approaching the safe upper limit.</span>
            )}
          </p>

          {calculationResult.suggestions.food.length > 0 && (
            <div className="suggestions-box">
              <h4>🍽️ Food Suggestions:</h4>
              <ul>{calculationResult.suggestions.food.map((s, i) => <li key={`food-${i}`}>{s}</li>)}</ul>
            </div>
          )}
          {calculationResult.suggestions.lifestyle.length > 0 && (
            <div className="suggestions-box">
              <h4>🌿 Lifestyle & Diet Tips:</h4>
              <ul>{calculationResult.suggestions.lifestyle.map((s, i) => <li key={`life-${i}`}>{s}</li>)}</ul>
            </div>
          )}
          {calculationResult.suggestions.genetic.length > 0 && (
            <div className="suggestions-box">
              <h4>🧬 Genetic Considerations (BCO1):</h4>
              <ul>{calculationResult.suggestions.genetic.map((s, i) => <li key={`gene-${i}`}>{s}</li>)}</ul>
            </div>
          )}
          {calculationResult.suggestions.supplement.length > 0 && (
            <div className="suggestions-box">
              <h4>💊 Supplement Considerations (Discuss with Doctor FIRST):</h4>
              <ul>{calculationResult.suggestions.supplement.map((s, i) => <li key={`supp-${i}`}>{s}</li>)}</ul>
            </div>
          )}
          {calculationResult.suggestions.general.length > 0 && (
            <div className="suggestions-box">
              <h4>⭐ General Notes:</h4>
              <ul>{calculationResult.suggestions.general.map((s, i) => <li key={`gen-${i}`}>{s}</li>)}</ul>
            </div>
          )}

          <h3>📝 Calculation Explanation</h3>
          <pre className="explanation-box">{calculationResult.explanation}</pre>

          <p className="important-note">
            <strong>Reminder:</strong> RAE standardizes sources of vitamin A. Conversion from plant carotenoids varies widely; cooking, fat, and genetics matter.
            Always consult your healthcare provider for personal advice.
          </p>
          <a href="#vita-education" className="scroll-link">Learn more about Vitamin A & Carotenoids below</a>
        </div>
      )}

      {/* --- Educational Content --- */}
      <div id="vita-education" className="educational-section">
        <h2>Understanding Vitamin A & Carotenoids</h2>

        <div className="content-block">
          <h3>1. Vitamin A Essentials</h3>
          <dl>
            <dt>Forms & Sources:</dt>
            <dd>
              <ul>
                <li><strong>Pre-formed Vitamin A (Retinoids):</strong> retinol/retinyl esters in animal foods (liver, fish oils, eggs, dairy) and fortified foods/supplements.</li>
                <li><strong>Pro-vitamin A Carotenoids:</strong> β-carotene, α-carotene, β-cryptoxanthin in colorful fruits/vegetables (carrots, sweet potatoes, spinach, kale, pumpkin, mango).</li>
              </ul>
            </dd>
            <dt>Conversion to Active Form (RAE):</dt>
            <dd>
              Conversion depends on carotenoid type, source (supplement vs food), food matrix, dietary fat, vitamin A status, and genetics (BCO1). Intake is expressed as <strong>Retinol Activity Equivalents (RAE)</strong>.
            </dd>
            <dt>Key Roles:</dt>
            <dd>
              Vision (rhodopsin), epithelial health, immunity, growth & development.
            </dd>
            <dt>Safety & UL:</dt>
            <dd>
              Pre-formed vitamin A has an age-specific UL (adults: 3,000 µg RAE/day). Pro-vitamin A carotenoids have no UL from foods; high-dose β-carotene supplements are not advised for smokers.
            </dd>
          </dl>
        </div>

        <div className="content-block">
          <h3>2. RAE Conversion Factors</h3>
          <p>Standard factors to convert intake to µg RAE:</p>
          <table>
            <thead>
              <tr><th>Source Form (1 µg of...)</th><th>Equals</th></tr>
            </thead>
            <tbody>
              <tr><td>Retinol (food/supplement)</td><td>1 µg RAE</td></tr>
              <tr><td>β-carotene (supplement)</td><td>0.5 µg RAE (or 2 µg βC = 1 µg RAE)</td></tr>
              <tr><td>β-carotene (food)</td><td>~0.083 µg RAE (or 12 µg βC = 1 µg RAE)</td></tr>
              <tr><td>α-carotene (food)</td><td>~0.042 µg RAE (or 24 µg = 1 µg RAE)</td></tr>
              <tr><td>β-cryptoxanthin (food)</td><td>~0.042 µg RAE (or 24 µg = 1 µg RAE)</td></tr>
            </tbody>
          </table>
          <small>Individual variability is large; these are population averages.</small>
        </div>

        <div className="content-block">
          <h3>3. Reference Intakes (RDA / AI*)</h3>
          <p>Values in µg RAE/day.</p>
          <table>
            <thead>
              <tr><th>Life Stage Group</th><th>Intake</th></tr>
            </thead>
            <tbody>
              <tr><td>Infants 0–6 months*</td><td>{vitARdaTable['0-6m'].target}</td></tr>
              <tr><td>Infants 7–12 months*</td><td>{vitARdaTable['7-12m'].target}</td></tr>
              <tr><td>Children 1–3 years</td><td>{vitARdaTable['1-3y'].target}</td></tr>
              <tr><td>Children 4–8 years</td><td>{vitARdaTable['4-8y'].target}</td></tr>
              <tr><td>Children 9–13 years</td><td>{vitARdaTable['9-13y'].target}</td></tr>
              <tr><td>Females 14–18 years</td><td>{vitARdaTable['14-18y_female'].target}</td></tr>
              <tr><td>Males 14–18 years</td><td>{vitARdaTable['14-18y_male'].target}</td></tr>
              <tr><td>Females 19+ years</td><td>{vitARdaTable['19+y_female'].target}</td></tr>
              <tr><td>Males 19+ years</td><td>{vitARdaTable['19+y_male'].target}</td></tr>
              <tr><td>Pregnancy (14–18y)</td><td>{vitARdaTable['pregnant_14-18'].target}</td></tr>
              <tr><td>Pregnancy (19+y)</td><td>{vitARdaTable['pregnant_19+'].target}</td></tr>
              <tr><td>Lactation (14–18y)</td><td>{vitARdaTable['lactating_14-18'].target}</td></tr>
              <tr><td>Lactation (19+y)</td><td>{vitARdaTable['lactating_19+'].target}</td></tr>
            </tbody>
          </table>
          <small>*Adequate Intake (AI)</small>
        </div>

        <div className="content-block">
          <h3>4. Groups Requiring Special Attention</h3>
          <ul>
            <li><strong>Planning Pregnancy / Pregnant:</strong> Meet RDA (≈750–770 µg RAE) but avoid liver and supplements containing pre-formed vitamin A; age-specific UL is 2,800–3,000 µg RAE.</li>
            <li><strong>Strict Vegans / Plant-forward:</strong> Rely entirely on carotenoid conversion—pair with dietary fat and consider cooking/chopping to improve absorption.</li>
            <li><strong>BCO1 Variants:</strong> Common variants can lower conversion; may need higher carotenoid intake and/or modest pre-formed sources (clinician-guided).</li>
            <li><strong>Fat Malabsorption:</strong> IBD, celiac, pancreatic insufficiency, cholestasis, bariatric surgery—monitor fat-soluble vitamins.</li>
            <li><strong>Liver Disease / High Alcohol:</strong> Affects storage and toxicity risk—avoid high-dose supplements unless prescribed.</li>
            <li><strong>Smokers:</strong> Avoid high-dose β-carotene supplements; food sources are preferred.</li>
          </ul>
        </div>

        <div className="content-block">
          <h3>Sources</h3>
          <p className="sources">
            NIH ODS Vitamin A Fact Sheet (Health Professionals & Consumers);<br />
            Institute of Medicine (2001) DRIs for Vitamin A;<br />
            Peer-reviewed literature on BCO1 variants and β-carotene trials (ATBC/CARET);<br />
            USDA FoodData Central for food values. (Replace access dates as needed.)
          </p>
        </div>
      </div>
      {/* End educational-section */}
      {/* End vitaminACarotenoidCalculator */}
    </div>
  );
}

export default VitaminACarotenoidCalculator;
